import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import { setDayjsLocale } from './format-date';

const CDN_URL = import.meta.env.VITE_CDN_URL || '';
const I18N_FOLDER = import.meta.env.VITE_I18N_FOLDER || 'i18n/scio30-beta';
// Strict `=== 'true'`: env values are strings, so a truthiness check would read
// `VITE_I18N_LOCAL=false` as ON.
const I18N_LOCAL = import.meta.env.VITE_I18N_LOCAL === 'true';
const STORAGE_KEY = 'language';

/**
 * Where ONE language's catalogue comes from.
 *
 * Lokalise owns the translations and publishes them to the CDN; the repo's copies are a
 * subset — measured 2026-09-28, `de.json` and its siblings carry 2,914 keys against the
 * CDN's 8,308. English is the exception, because developers add English keys in code, so
 * the repo is AHEAD there (8,339) and is the only place a branch's new strings exist until
 * `i18n:sync-code-to-beta` runs, which happens only on a push to `staging`.
 *
 * So an environment that opts into local catalogues (`VITE_I18N_LOCAL`, set on qa3a) takes
 * ENGLISH from its own build — so the branch's new copy is visible instead of rendering as
 * raw keys — and every other language still from the CDN. Serving all languages locally
 * would trade 8,308 translated strings for 2,914 and silently fall the rest back to English.
 *
 * Dev is unchanged: everything local, so the app still works offline.
 */
export interface LocaleSourceEnv {
  dev: boolean;
  /** `VITE_I18N_LOCAL === 'true'`. */
  local: boolean;
  cdnUrl: string;
  folder: string;
}

const BUILD_ENV: LocaleSourceEnv = {
  dev: import.meta.env.DEV,
  local: I18N_LOCAL,
  cdnUrl: CDN_URL,
  folder: I18N_FOLDER,
};

/** Exported with an injectable env so the test exercises this function, not a copy of it. */
export function localeUrl(lng: string, env: LocaleSourceEnv = BUILD_ENV): string {
  const cdnBase = env.cdnUrl ? `${env.cdnUrl}/${env.folder}` : '';
  if (env.dev) return `/i18n/${lng}.json`;
  if (env.local) return lng !== 'en' && cdnBase ? `${cdnBase}/${lng}.json` : `/i18n/${lng}.json`;
  return cdnBase ? `${cdnBase}/${lng}.json` : `/i18n/${lng}.json`;
}

i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    lng: localStorage.getItem(STORAGE_KEY) || undefined,
    fallbackLng: 'en',
    supportedLngs: ['en', 'de', 'es', 'fr', 'it', 'ja', 'nl', 'pt', 'sv'],
    // Our catalogues are FLAT (one object of string values) and keys are usually the English
    // sentence. Dots are already safe under the defaults: i18next 25 ships
    // `ignoreJSONStructure: true`, so a flat `dunning.banner.dated` or a sentence ending in `.`
    // resolves either way. What the defaults break is the COLON: `nsSeparator: ':'` reads
    // `16:9` as namespace `16` + key `9`, so `16:9` renders `9`, `4:3` renders `3`,
    // `Preview:` / `Selected:` / `Detail:` render empty, and `https://` renders `//`
    // (8 keys per locale in the CDN catalogues). Turning both separators off makes every key
    // a literal string, which is what a flat catalogue means. Nothing here uses namespaces,
    // `keyPrefix` or `returnObjects`, so nothing depends on either separator.
    keySeparator: false,
    nsSeparator: false,
    interpolation: {
      escapeValue: false, // React already escapes
    },
    backend: {
      loadPath: (lngs: readonly string[]) => localeUrl(lngs[0]),
    },
    react: {
      useSuspense: false, // Avoid flash — show stale text until loaded
      bindI18nStore: 'added removed', // Re-render when HttpBackend loads a new language bundle
    },
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: STORAGE_KEY,
    },
  });

const SUPPORTED_LANGS = ['en', 'de', 'es', 'fr', 'it', 'ja', 'nl', 'pt', 'sv'];

// Preload all languages in background so switching is instant
i18n.on('initialized', () => {
  i18n.loadLanguages(SUPPORTED_LANGS);
  setDayjsLocale(i18n.language);
});

// Sync <html lang="..."> and dayjs global locale with i18next language
i18n.on('languageChanged', (lng: string) => {
  document.documentElement.lang = lng;
  setDayjsLocale(lng);
});

export { STORAGE_KEY };
export default i18n;
